import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import moment from "moment";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  nlNL,
  enUS,
} from "@mui/x-data-grid";
import { Typography } from "@mui/material";

import AppContext from "../../appContext";
import ChooseLabels from "../ChooseLabels";
import { getLabelId } from "../moodboardModel";

function CustomToolbar({
  variant,
  table,
  selectedLabels,
  handleSelectLabels,
  initialMoodboard,
}) {
  const {
    app: {
      client: { alias },
    },
  } = useContext(AppContext);
  return (
    <GridToolbarContainer>
      <ChooseLabels
        variant={variant}
        initTable={table}
        selectedLabels={selectedLabels}
        handleSelectLabels={handleSelectLabels}
        initialSelectedLabels={initialMoodboard.labelProps.map((prop) =>
          getLabelId(prop)
        )}
      />
      <Box padding={1} />
      <GridToolbarExport
        csvOptions={{
          fileName: `m-Path client ${alias} - ${moment().format(
            "dd DD/MM/YYYY - HH:mm"
          )}`,
          delimiter: ";",
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  );
}

function CustomColumn(props) {
  const { field, value, colDef, labels } = props;
  const getValue = () => {
    //const row = labels.filter((label) => label.id === field)?.[0];
    let val = Array.isArray(value) ? value.join(", ") : value;
    //if (row?.type === "time") val = moment.utc(val * 1000).format("HH:mm");
    return val;
  };
  return (
    <div>
      <Typography
        variant="body2"
        sx={{
          textarea: { resize: "both" },
          //width: colDef.computedWidth,
          wordBreak: "break-word",
        }}
      >
        {getValue()}
      </Typography>
    </div>
  );
}

export default function TableBoard({
  data,
  quantitativeLabelsChecked,
  qualitativeLabelsChecked,
  variant,
  table,
  selectedLabels,
  handleSelectLabels,
  initialMoodboard,
  hideEmptyRows,
}) {
  const { i18n } = useTranslation();
  const getLang = () => {
    if (i18n.language === "nl") return nlNL;
    return enUS;
  };
  const [pageSize, setPageSize] = React.useState(5);
  const [isShown, setIsShown] = React.useState(false);
  // columns
  const humanTimeColumn = {
    field: "humanTime",
    headerName: "Time",
    width: 180,
    valueGetter: (params) => {
      return moment.utc(params.row.time).format("dd DD/MM/YYYY - HH:mm");
    },
    sortComparator: (v1, v2, param1, param2) =>
      param1.api.getCellValue(param1.id, "time") -
      param2.api.getCellValue(param2.id, "time"),
    valueFormatter: ({ value }) => value,
    renderCell: ({ value }) => value,
  };
  const labelColumns = [
    ...quantitativeLabelsChecked,
    ...qualitativeLabelsChecked,
  ].map(({ id, label }) => ({
    field: id,
    headerName: label,
    width: 200,
    renderCell: (params) => (
      <CustomColumn
        labels={[...quantitativeLabelsChecked, ...qualitativeLabelsChecked]}
        {...params}
      />
    ),
  }));
  const columns = [humanTimeColumn, ...labelColumns];

  React.useEffect(() => {
    if (variant !== "timeline" && !isShown) {
      const timer = setTimeout(() => {
        setIsShown(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (variant === "timeline") setIsShown(true);
  }, [isShown]);

  const getData = () => {
    // filter empty rows out the data
    if (hideEmptyRows) {
      return data.filter((row) => {
        // check if selected label exists in row
        let keyExists = false;
        selectedLabels.forEach((label) => {
          if (Object.keys(row).includes(label)) {
            keyExists = true;
          }
        });
        return keyExists;
      });
    }
    return data;
  };

  return (
    <div>
      {isShown ? (
        <DataGrid
          localeText={getLang().components.MuiDataGrid.defaultProps.localeText}
          autoHeight
          density="compact"
          rows={getData()}
          columns={columns}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          onPageSizeChange={(size) => setPageSize(size)}
          pageSize={pageSize}
          disableColumnFilter={false}
          checkboxSelection={false}
          disableSelectionOnClick
          disableColumnMenu
          disableColumnSelector
          components={{
            Toolbar: CustomToolbar,
          }}
          getRowHeight={() => "auto"}
          componentsProps={{
            toolbar: {
              variant,
              table,
              selectedLabels,
              handleSelectLabels,
              initialMoodboard,
            },
          }}
        />
      ) : null}
    </div>
  );
}
